import { Fragment } from "react";
import { BotIcon } from "lucide-react";
import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { SidebarMenuSubItem } from "../ui/sidebar";
import type { SidebarThreadSummary } from "../../types";
import { SidebarThreadRow, type SidebarThreadRowProps } from "./SidebarThreadRow";
import { SidebarThreadAgentSummary, SidebarThreadWorkerToggle } from "./SidebarThreadWorkerSummary";
import { WorkerSubRow } from "./WorkerSubRow";
import type { WorkerSubRowItem } from "./sidebarWorkerRows.logic";
import { stableTaskAgentName } from "../../lib/taskAgentIdentity";
import { useTaskWorkItemKey } from "./TaskWorkItemKeyContext";

export interface SidebarMasterWorkerData {
  readonly workers: readonly WorkerSubRowItem[];
  readonly attention: number;
  readonly environmentId: string;
}

type SidebarThreadRowSharedProps = Omit<
  SidebarThreadRowProps,
  | "thread"
  | "projectCwd"
  | "orderedProjectThreadKeys"
  | "isActive"
  | "jumpLabel"
  | "workerCount"
  | "attentionCount"
  | "workersExpanded"
  | "onToggleWorkers"
  | "isPinned"
  | "onTogglePinned"
>;

interface SidebarReadOnlyThreadRowProps {
  readonly thread: SidebarThreadSummary;
  readonly reason: string;
  readonly threadKey: string;
  readonly workerCount: number;
  readonly attentionCount: number;
  readonly workersExpanded: boolean;
  readonly onToggleWorkers: (threadKey: string) => void;
}

function SidebarReadOnlyThreadRow({
  thread,
  reason,
  threadKey,
  workerCount,
  attentionCount,
  workersExpanded,
  onToggleWorkers,
}: SidebarReadOnlyThreadRowProps) {
  const agentName =
    thread.kind === "master" && thread.anchorKind === "task"
      ? stableTaskAgentName(thread.id)
      : null;
  const resolvedWorkItemKey = useTaskWorkItemKey(thread.anchorCmmdId);
  const workItemKey =
    thread.kind === "master" && thread.anchorKind === "task" ? resolvedWorkItemKey : null;
  return (
    <SidebarMenuSubItem className="w-full">
      <div
        aria-disabled="true"
        title={`${thread.title} · ${reason}`}
        className="flex h-7 w-full min-w-0 items-center px-2 text-muted-foreground/65"
      >
        <span className="flex min-w-0 flex-1 items-center gap-1 truncate text-xs">
          {agentName !== null ? (
            <BotIcon
              className="size-3 shrink-0 text-primary"
              aria-label={`${agentName}, AI task owner`}
            />
          ) : null}
          <span className="truncate">
            {agentName !== null ? `${agentName} - ${thread.title}` : thread.title}
          </span>
        </span>
        {workItemKey !== null ? (
          <span
            aria-label={`Work item ${workItemKey}`}
            className="ml-1 rounded border border-border/70 bg-muted/70 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-muted-foreground"
          >
            {workItemKey}
          </span>
        ) : null}
        <SidebarThreadAgentSummary workerCount={workerCount} attentionCount={attentionCount} />
        <SidebarThreadWorkerToggle
          threadKey={threadKey}
          threadId={thread.id}
          workerCount={workerCount}
          workersExpanded={workersExpanded}
          reserveArchiveSpace={false}
          onToggleWorkers={onToggleWorkers}
          onPointerDown={(event) => event.stopPropagation()}
        />
      </div>
    </SidebarMenuSubItem>
  );
}

interface SidebarProjectThreadEntryProps {
  readonly thread: SidebarThreadSummary;
  readonly projectCwd: string;
  readonly orderedProjectThreadKeys: readonly string[];
  readonly activeRouteThreadKey: string | null;
  readonly threadJumpLabelByKey: ReadonlyMap<string, string>;
  readonly masterData: SidebarMasterWorkerData | undefined;
  readonly expandedWorkersByThread: ReadonlySet<string>;
  readonly onToggleWorkers: (threadKey: string) => void;
  readonly onNavigateToWorker: (workerId: string, environmentId: string) => void;
  readonly pinnedThreadKeys: ReadonlySet<string>;
  readonly onTogglePinned: (threadKey: string) => void;
  readonly readOnlyReason: string | null;
  readonly threadRowProps: SidebarThreadRowSharedProps;
}

function SidebarProjectThreadEntry(props: SidebarProjectThreadEntryProps) {
  const threadKey = scopedThreadKey(scopeThreadRef(props.thread.environmentId, props.thread.id));
  const workersExpanded = props.expandedWorkersByThread.has(threadKey);
  const workerCount = props.masterData?.workers.length ?? 0;
  const attentionCount = props.masterData?.attention ?? 0;

  return (
    <Fragment>
      {props.readOnlyReason ? (
        <SidebarReadOnlyThreadRow
          thread={props.thread}
          reason={props.readOnlyReason}
          threadKey={threadKey}
          workerCount={workerCount}
          attentionCount={attentionCount}
          workersExpanded={workersExpanded}
          onToggleWorkers={props.onToggleWorkers}
        />
      ) : (
        <SidebarThreadRow
          thread={props.thread}
          projectCwd={props.projectCwd}
          orderedProjectThreadKeys={props.orderedProjectThreadKeys}
          isActive={props.activeRouteThreadKey === threadKey}
          jumpLabel={props.threadJumpLabelByKey.get(threadKey) ?? null}
          workerCount={workerCount}
          attentionCount={attentionCount}
          workersExpanded={workersExpanded}
          onToggleWorkers={props.onToggleWorkers}
          isPinned={props.pinnedThreadKeys.has(threadKey)}
          onTogglePinned={props.onTogglePinned}
          {...props.threadRowProps}
        />
      )}
      {props.masterData && workersExpanded
        ? props.masterData.workers.map((worker) => (
            <WorkerSubRow
              key={worker.id}
              id={worker.id}
              title={worker.title}
              status={worker.status}
              workerRole={worker.workerRole ?? null}
              onNavigate={(id) =>
                props.onNavigateToWorker(
                  id,
                  props.masterData?.environmentId ?? props.thread.environmentId,
                )
              }
            />
          ))
        : null}
    </Fragment>
  );
}

export interface SidebarProjectThreadSectionProps {
  readonly threads: readonly SidebarThreadSummary[];
  readonly projectCwd: string;
  readonly orderedProjectThreadKeys: readonly string[];
  readonly activeRouteThreadKey: string | null;
  readonly threadJumpLabelByKey: ReadonlyMap<string, string>;
  readonly workersByMasterKey: ReadonlyMap<string, SidebarMasterWorkerData>;
  readonly expandedWorkersByThread: ReadonlySet<string>;
  readonly onToggleWorkers: (threadKey: string) => void;
  readonly onNavigateToWorker: (workerId: string, environmentId: string) => void;
  readonly pinnedThreadKeys: ReadonlySet<string>;
  readonly onTogglePinned: (threadKey: string) => void;
  readonly readOnlyReason: string | null;
  readonly threadRowProps: SidebarThreadRowSharedProps;
}

export function SidebarProjectThreadSection(props: SidebarProjectThreadSectionProps) {
  return (
    <Fragment>
      {props.threads.map((thread) => (
        <SidebarProjectThreadEntry
          key={scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id))}
          thread={thread}
          projectCwd={props.projectCwd}
          orderedProjectThreadKeys={props.orderedProjectThreadKeys}
          activeRouteThreadKey={props.activeRouteThreadKey}
          threadJumpLabelByKey={props.threadJumpLabelByKey}
          masterData={props.workersByMasterKey.get(thread.id)}
          expandedWorkersByThread={props.expandedWorkersByThread}
          onToggleWorkers={props.onToggleWorkers}
          onNavigateToWorker={props.onNavigateToWorker}
          pinnedThreadKeys={props.pinnedThreadKeys}
          onTogglePinned={props.onTogglePinned}
          readOnlyReason={props.readOnlyReason}
          threadRowProps={props.threadRowProps}
        />
      ))}
    </Fragment>
  );
}
